import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import Loader from '../Shared/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faUser, faNoteSticky, faSave, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const EditSubscription = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const subscription = location.state;

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [plans, setPlans] = useState([]);

    useEffect(() => {
        if (!subscription) {
            toastHelper.error('لا توجد بيانات للاشتراك المحدد');
            navigate('/admin/dashboard');
            return;
        }
    }, [subscription, navigate]);

    const validationSchema = Yup.object({
        plan_id: Yup.string().required('يرجى اختيار الباقة'),
        notes: Yup.string().max(500, 'الملاحظات يجب أن لا تتجاوز 500 حرف')
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            user_id: subscription?.user_id ? String(subscription.user_id) : '',
            plan_id: subscription?.plan_id ? String(subscription.plan_id) : '',
            notes: subscription?.notes || ''
        },
        validationSchema,
        onSubmit: async (values) => {
            setSubmitting(true);
            try {
                await ApiServices.put(`Subscription/User/update/${id}`, {
                    plan_id: values.plan_id,
                    notes: values.notes,
                    user_id: values.user_id
                });
                toastHelper.success('تم تحديث الاشتراك بنجاح');
                navigate('/admin/dashboard');
            } catch (error) {
                toastHelper.error(error.message || error.msg || 'فشل في تحديث الاشتراك');
            } finally {
                setSubmitting(false);
            }
        }
    });

    const customSelectStyles = {
        container: (base) => ({
            ...base,
            width: '100%',
        }),
        control: (base, state) => ({
            ...base,
            padding: '6px',
            borderRadius: '12px',
            border: state.isFocused ? '2px solid #6366f1' : '2px solid #e2e8f0',
            boxShadow: state.isFocused ? '0 0 0 4px rgba(99, 102, 241, 0.1)' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#6366f1' : '#cbd5e1'
            },
            fontFamily: 'Zain, sans-serif'
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#6366f1' : state.isFocused ? '#f1f5f9' : 'white',
            color: state.isSelected ? 'white' : '#334155',
            '&:active': {
                backgroundColor: '#6366f1'
            },
            fontFamily: 'Zain, sans-serif'
        }),
        placeholder: (base) => ({
            ...base,
            fontFamily: 'Zain, sans-serif',
            color: '#94a3b8'
        }),
        singleValue: (base) => ({
            ...base,
            fontFamily: 'Zain, sans-serif',
            color: '#1e293b'
        })
    };

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const plansRes = await ApiServices.get('Lookup/Plans');
            if (Array.isArray(plansRes)) setPlans(plansRes);
        } catch (error) {
            toastHelper.error('فشل في تحميل بيانات الباقات');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (subscription) {
            fetchPlans();
        }
    }, [subscription]);

    if (!subscription) return null;
    if (loading) return <Loader text="جاري تحميل بيانات الاشتراك..." />;

    return (
        <div className="add-sub-page">
            <div className="form-container">
                <header className="form-header">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <FontAwesomeIcon icon={faArrowRight} />
                        <span>عودة</span>
                    </button>
                    <h1>تعديل اشتراك</h1>
                </header>

                <form onSubmit={formik.handleSubmit}>
                    <div className="form-grid">
                        <div className="input-group full-width">
                            <label>
                                <FontAwesomeIcon icon={faUser} />
                                المستخدم
                            </label>
                            <input
                                type="text"
                                value={subscription.user_name || ''}
                                disabled
                                className="readonly-input"
                            />
                        </div>

                        <div className="input-group full-width">
                            <label>
                                <FontAwesomeIcon icon={faTag} />
                                باقة الاشتراك
                            </label>
                            <Select
                                options={plans.map(p => ({ value: String(p.id), label: p.name }))}
                                placeholder="اختر الباقة..."
                                isRtl={true}
                                styles={customSelectStyles}
                                className={formik.touched.plan_id && formik.errors.plan_id ? 'select-error' : ''}
                                value={
                                    plans
                                        .map(p => ({ value: String(p.id), label: p.name }))
                                        .find(opt => opt.value === formik.values.plan_id) || null
                                }
                                onChange={(opt) => formik.setFieldValue('plan_id', opt ? opt.value : '')}
                            />
                            {formik.touched.plan_id && formik.errors.plan_id && (
                                <div className="field-error">{formik.errors.plan_id}</div>
                            )}
                        </div>

                        <div className="input-group full-width">
                            <label>
                                <FontAwesomeIcon icon={faNoteSticky} />
                                ملاحظات إضافية
                            </label>
                            <textarea
                                {...formik.getFieldProps('notes')}
                                placeholder="أي تفاصيل أو ملاحظات حول هذا الاشتراك..."
                                rows="4"
                                className={formik.touched.notes && formik.errors.notes ? 'input-error' : ''}
                            />
                            {formik.touched.notes && formik.errors.notes && (
                                <div className="field-error">{formik.errors.notes}</div>
                            )}
                        </div>
                    </div>

                    <footer className="form-footer">
                        <button
                            type="submit"
                            className="submit-btn"
                            disabled={submitting}
                        >
                            {submitting ? 'جاري التحديث...' : (
                                <>
                                    <FontAwesomeIcon icon={faSave} />
                                    <span>تحديث الاشتراك</span>
                                </>
                            )}
                        </button>
                    </footer>
                </form>
            </div>

            <style>{`
                .add-sub-page {
                    padding: 40px 20px;
                    background: #f8fafc;
                    min-height: 100vh;
                    direction: rtl;
                      font-family: "Zain", sans-serif;
                }
                .form-container {
                    max-width: 800px;
                    margin: 0 auto;
                    background: white;
                    padding: 40px;
                    border-radius: 24px;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.05);
                }
                .form-header {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    margin-bottom: 40px;
                    border-bottom: 2px solid #f1f5f9;
                    padding-bottom: 20px;
                }
                .back-btn {
                    background: #f1f5f9;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    cursor: pointer;
                    color: #64748b;
                    font-weight: 600;
                    transition: all 0.2s;
                }
                .back-btn:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }
                .form-header h1 {
                    margin: 0;
                    font-size: 1.8rem;
                    color: #1e293b;
                    font-weight: 800;
                }
                .form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 30px;
                }
                .input-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .input-group.full-width {
                    grid-column: span 2;
                }
                .input-group label {
                    font-size: 0.95rem;
                    color: #475569;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .input-group input,
                .input-group textarea {
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    padding: 10px 14px;
                    font-size: 0.95rem;
                      font-family: "Zain", sans-serif;
                }
                .readonly-input {
                    background: #f8fafc;
                    color: #64748b;
                }
                .input-error {
                    border-color: #f97373;
                }
                .field-error {
                    color: #dc2626;
                    font-size: 0.8rem;
                    margin-top: 4px;
                }
                .form-footer {
                    margin-top: 30px;
                    display: flex;
                    justify-content: flex-end;
                }
                .submit-btn {
                    background: #6366f1;
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 999px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                    min-width: 180px;
                    justify-content: center;
                }
                .submit-btn:hover {
                    background: #4f46e5;
                }
                @media (max-width: 768px) {
                    .form-container {
                        padding: 24px;
                    }
                    .form-grid {
                        grid-template-columns: 1fr;
                    }
                    .input-group.full-width {
                        grid-column: span 1;
                    }
                }
            `}</style>
        </div>
    );
};

export default EditSubscription;

